import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type {
  MessageKind,
  MessagingTask,
  ComposeResult,
  CreateMessagingTaskRequest,
  CreateMessagingTaskResponse,
  MarkSentRequest,
  MessageTemplate,
  MessagingConfig,
} from '@guilleumes/types';

// Query keys
export const messagingKeys = {
  all: ['messaging'] as const,
  tasks: (reservationId?: string) =>
    [...messagingKeys.all, 'tasks', { reservationId: reservationId ?? null }] as const,
  task: (id: string) => [...messagingKeys.all, 'task', id] as const,
  templates: () => [...messagingKeys.all, 'templates'] as const,
  template: (id: string) => [...messagingKeys.templates(), id] as const,
  config: () => [...messagingKeys.all, 'config'] as const,
  logs: (reservationId?: string, clientId?: string) =>
    [...messagingKeys.all, 'logs', { reservationId, clientId }] as const,
};

// ═══════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════

export function useMessagingTasks(reservationId?: string) {
  return useQuery<MessagingTask[]>({
    queryKey: messagingKeys.tasks(reservationId),
    queryFn: async () => {
      const res = await api.get('/operational-tasks', {
        params: {
          category: 'messaging',
          reservationId,
        },
      });
      return res.data;
    },
    enabled: true,
  });
}

export function useMessageTemplates(kind?: MessageKind) {
  return useQuery<MessageTemplate[]>({
    // Amb `kind`, clau pròpia: la resposta només porta les d'aquell kind, i amb la clau comuna
    // el compositor d'ACCESS podia rebre de la cau les de WELCOME. Penja de `templates()`,
    // o sigui que invalidar `templates()` també les invalida.
    queryKey: kind ? [...messagingKeys.templates(), { kind }] : messagingKeys.templates(),
    queryFn: async () => {
      const res = await api.get('/messaging/templates', {
        params: kind ? { kind } : undefined,
      });
      return res.data;
    },
  });
}

export function useMessagingConfig() {
  return useQuery<MessagingConfig>({
    queryKey: messagingKeys.config(),
    queryFn: async () => {
      const res = await api.get('/messaging/config');
      return res.data;
    },
  });
}

// ═══════════════════════════════════════════════════════════════
// MUTATIONS
// ═══════════════════════════════════════════════════════════════

export function useCreateMessagingTask() {
  const queryClient = useQueryClient();

  return useMutation<CreateMessagingTaskResponse, Error, CreateMessagingTaskRequest>({
    mutationFn: async (data) => {
      const res = await api.post('/messaging/tasks', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.all });
    },
  });
}

export function useComposeMessage() {
  const queryClient = useQueryClient();

  return useMutation<
    ComposeResult,
    Error,
    { taskId: string; manualVariables?: Record<string, string>; textOverride?: string }
  >({
    mutationFn: async ({ taskId, manualVariables, textOverride }) => {
      const res = await api.post(`/messaging/tasks/${taskId}/compose`, {
        manualVariables,
        textOverride,
      });
      return res.data;
    },
    // `/compose` desa a la tasca l'enllaç, el text i les variables (p. ex. el codi escrit a mà).
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.all });
    },
  });
}

export function useMarkAsSent() {
  const queryClient = useQueryClient();

  return useMutation<MessagingTask, Error, { taskId: string; data: MarkSentRequest }>({
    mutationFn: async ({ taskId, data }) => {
      const res = await api.post(`/messaging/tasks/${taskId}/mark-sent`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.all });
      // «Precontacte Booking»: la fila de «Per escriure» marxa sola quan la benvinguda surt.
      queryClient.invalidateQueries({ queryKey: ['precontact-booking'] });
    },
  });
}

export function useMarkAsUnsent() {
  const queryClient = useQueryClient();

  return useMutation<MessagingTask, Error, string>({
    mutationFn: async (taskId) => {
      const res = await api.post(`/messaging/tasks/${taskId}/mark-unsent`, {});
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.all });
      // «Precontacte Booking»: en desmarcar, la fila torna a «Per escriure».
      queryClient.invalidateQueries({ queryKey: ['precontact-booking'] });
    },
  });
}

export function useMarkNoWhatsapp() {
  const queryClient = useQueryClient();

  return useMutation<MessagingTask, Error, string>({
    mutationFn: async (taskId) => {
      const res = await api.post(`/messaging/tasks/${taskId}/mark-no-whatsapp`, {});
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.all });
    },
  });
}

export function useUpdateMessagingConfig() {
  const queryClient = useQueryClient();

  return useMutation<MessagingConfig, Error, Partial<MessagingConfig>>({
    mutationFn: async (data) => {
      const res = await api.patch('/messaging/config', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.config() });
    },
  });
}

// ═══════════════════════════════════════════════════════════════
// COMPOSITE HOOKS
// ═══════════════════════════════════════════════════════════════

export function useMessagingForReservation(reservationId: string) {
  const { data: tasks, isLoading: tasksLoading } = useMessagingTasks(reservationId);
  const { data: templates, isLoading: templatesLoading } = useMessageTemplates();
  const { data: config, isLoading: configLoading } = useMessagingConfig();

  const createTask = useCreateMessagingTask();
  const compose = useComposeMessage();
  const markSent = useMarkAsSent();
  const markUnsent = useMarkAsUnsent();
  const markNoWhatsapp = useMarkNoWhatsapp();

  // Filter tasks by reservation
  const reservationTasks = tasks?.filter((t) => t.reservationId === reservationId) ?? [];

  // Group by kind
  const tasksByKind = reservationTasks.reduce(
    (acc, task) => {
      const kind = task.messageKind;
      if (!acc[kind]) acc[kind] = [];
      acc[kind].push(task);
      return acc;
    },
    {} as Record<MessageKind, MessagingTask[]>,
  );

  // Get latest task per kind
  const latestByKind = Object.entries(tasksByKind).reduce(
    (acc, [kind, kindTasks]) => {
      const latest = kindTasks.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )[0];
      acc[kind as MessageKind] = latest;
      return acc;
    },
    {} as Partial<Record<MessageKind, MessagingTask>>,
  );

  return {
    // Data
    tasks: reservationTasks,
    tasksByKind,
    latestByKind,
    templates,
    config,

    // Loading states
    isLoading: tasksLoading || templatesLoading || configLoading,

    // Actions
    createTask: createTask.mutateAsync,
    composeMessage: compose.mutateAsync,
    markAsSent: markSent.mutateAsync,
    markAsUnsent: markUnsent.mutateAsync,
    markNoWhatsapp: markNoWhatsapp.mutateAsync,

    // Mutation states
    isCreating: createTask.isPending,
    isComposing: compose.isPending,
    isMarking: markSent.isPending || markUnsent.isPending || markNoWhatsapp.isPending,
  };
}
